// Rethinking the Warm-Up. A free clinic from Coach Tyler Clark at the San Diego
// coaches retreat, on using the warm-up to build connection, readiness, and
// culture. Mux: 6PcYC00XPFU6QJnhLdpOAtd2nZJAHbLRa45hZpXZD33Y
// Lesson cards authored from the Mux auto-caption transcript. No em dashes.

export const WARMUP_MODULE_KEY = "rethinking-the-warmup";

const VIDEO = "6PcYC00XPFU6QJnhLdpOAtd2nZJAHbLRa45hZpXZD33Y";

export const WARMUP_MODULE = {
  key: WARMUP_MODULE_KEY,
  title: "Rethinking the Warm-Up",
  sub: "Coach Tyler Clark on turning the warm-up into a tool for connection, readiness, and culture, not just sweat.",
  coverVideoId: VIDEO,
  duration: "Full clinic plus 8 lessons",
  chapters: [
    {
      id: "watch",
      title: "Watch the clinic",
      blocks: [
        { kind: "text", md:
          "A live clinic from Coach Tyler Clark at the retreat. He runs a traditional warm-up, then a connection-based one, and has the room feel the difference. Watch it through. The cards below pull out the ideas in order." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Rethinking the Warm-Up. Coach Tyler Clark. Full clinic." },
      ],
    },
    {
      id: "more-than-you-think",
      title: "1. The warm-up is doing more than you think.",
      blocks: [
        { kind: "text", md:
          "**A traditional dynamic warm-up gets players physically warm and leaves everything else on the table.**" },
        { kind: "text", md:
          "Soldier kicks, karaoke, skips. You break a sweat and you feel loose. But ask the harder question. Did it connect the group, did it prepare anyone mentally and emotionally, did it get the brain switched on. Most of the time, it did not." },
        { kind: "text", md:
          "The traditional warm-up is not bad. The point is that we can get a lot more out of the same five to seven minutes if we are deliberate about it." },
      ],
    },
    {
      id: "warm-to-connect",
      title: "2. Warm to connect, not just to sweat.",
      blocks: [
        { kind: "text", md:
          "**Get athletes involved with each other before the ball ever comes out.**" },
        { kind: "text", md:
          "In most settings, a training group, a private, a new team, you have people who have never met, or a core few who only know each other. Partner and small-group work in the warm-up integrates them and starts building the team on minute one." },
        { kind: "text", md:
          "Mirroring drills, balance flows, a quick partner battle. The exercise is almost beside the point. The job is to get them interacting." },
      ],
    },
    {
      id: "contact-builds-trust",
      title: "3. Contact builds trust.",
      blocks: [
        { kind: "text", md:
          "**Physical touch builds trust, confidence, and emotional regulation. You can engineer it into the warm-up.**" },
        { kind: "text", md:
          "There is research on this. Across a large set of free throw attempts, a simple high five or fist bump from a teammate after a miss raised the make rate on the next one by a striking amount. The contact is nonverbal communication. It says I see you, you belong here, you are doing a good job." },
        { kind: "text", md:
          "So build contact in on purpose. Partner mirrors, tug of war, a balance bump where you try to make your partner put a second foot down. Anytime you can add safe, intentional contact, you are manufacturing trust." },
      ],
    },
    {
      id: "competitive-and-reactive",
      title: "4. Make it competitive and reactive.",
      blocks: [
        { kind: "text", md:
          "**Fun, competition, and reaction wake up the brain along with the body.**" },
        { kind: "text", md:
          "The connection warm-up lands because it is competitive and reactive. A single-leg bump game for points. A foot-tag in a split stance. A get-off-the-ground tag where you can roll or bear crawl. Players are engaged, not going through motions." },
        { kind: "text", md:
          "You can also just add reactivity to a traditional warm-up. Keep the movements, add a partner and a decision. You get the movement prep plus the problem solving, and the energy walks up with it." },
      ],
    },
    {
      id: "no-performance-background",
      title: "5. You do not need a performance background.",
      blocks: [
        { kind: "text", md:
          "**Just putting a partner in front of an athlete adds the emotional and psychological piece.**" },
        { kind: "text", md:
          "Some of the partner work comes easier if you have a performance background, and that is fine. You do not need it. Find the version that works for you and your group, even if it is simple. A partner, a reaction, a little contact." },
        { kind: "text", md:
          "Do not let it feel overwhelming. The traditional warm-up is a fine starting point. The goal is just to do a little more with it." },
      ],
    },
    {
      id: "match-the-day",
      title: "6. Match the warm-up to the day.",
      blocks: [
        { kind: "text", md:
          "**The intense connection warm-up is not always the answer. Read the day.**" },
        { kind: "text", md:
          "If it is a high-energy, intense session, the interactive battle warm-up is a great fit. If it is a lower day, a more traditional dynamic warm-up might serve better, maybe with a touch of reactivity added." },
        { kind: "text", md:
          "Part of the craft is the discernment to use both at the right times rather than running the same thing every day." },
      ],
    },
    {
      id: "design-within-constraints",
      title: "7. Design within your constraints.",
      blocks: [
        { kind: "text", md:
          "**You can build a great warm-up in a hallway, a loud gym, or a conference room with one chair.**" },
        { kind: "text", md:
          "The coaches got handed real constraints. A tiny hallway on an away trip. A loud clinic where every drill needs a ball and nobody can shoot. A 6:30am conference room with tired, traveled players and a single chair. Each group built something that worked." },
        { kind: "text", md:
          "Constraints are not the obstacle. They force the creativity. The space and gear you have is enough if you are deliberate about how you use it." },
      ],
    },
    {
      id: "warmup-is-culture",
      title: "8. The warm-up is culture.",
      blocks: [
        { kind: "text", md:
          "**This is not only a team tool. It builds rapport and culture in any session.**" },
        { kind: "text", md:
          "It is easy to file this under team practice in your mind, but it works just as well in a player-development session. The warm-up is an opportunity to build culture and connection with the people in front of you." },
        { kind: "text", md:
          "Five minutes of real connection compounds over a season. How connected the group feels walking into the day is a different feeling than getting your soldier kicks in and moving on." },
      ],
    },
  ],
};
